import { MatricesShell } from '@/components/app-shell';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { NoyauAccentScope } from '@/components/noyau-accent-scope';
import { SettingsShortcut } from '@/components/settings/settings-shortcut';
import { isAuthenticated } from '@/lib/auth-guard';
import { useTweakByKey } from '@/lib/tweak-store';
import { cn } from '@/lib/utils';
import type { NoyauColorToken } from '@mss_creator/shared';
import { Outlet, createRootRoute, redirect, useLocation } from '@tanstack/react-router';

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    // `/login` is the only unguarded route.
    if (location.pathname === '/login') {
      return;
    }

    // Every other route requires a session; resolve once on boot, then gate on
    // the local token for subsequent navigations (see lib/auth-guard).
    if (!(await isAuthenticated())) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
  },
  component: RootComponent,
});

/**
 * The 4 company-context routes scoped by the `matrix-noyaux-accent`
 * settings tweak (point 16, 2026-08-18 grill) — company detail/settings, the creation
 * wizard, the matrix view, and its export page. `/companies/new` is
 * deliberately excluded: it isn't a company-context page yet (no company to
 * theme).
 */
function isNoyauAccentScopedRoute(pathname: string): boolean {
  if (pathname.startsWith('/companies/') && pathname !== '/companies/new') return true;
  if (pathname.startsWith('/wizard/')) return true;
  if (pathname.startsWith('/matrices/')) return true;
  return false;
}

/**
 * App shell: the design system's `AppShell` (`MatricesShell`), whose `main`
 * is a full-height flex column under its 56 px header.
 *
 * The content column is hard-capped to the viewport left under that header
 * (`calc(100vh - 56px)`, not a `min-h-*`): a `min-h-*` only sets a floor,
 * so once page content grew taller than the viewport the whole document
 * scrolled, dragging the matrix page's breadcrumb/title/toolbar off-screen
 * with it. Capping the height and relying on `min-h-0` + `flex-1` down the
 * tree forces any deliberately-scrollable descendant to become the actual
 * scroll region instead of the page itself.
 *
 * `min-w-0` keeps a wide page (the export route's preview, a long
 * breadcrumb) from pushing the column past the viewport.
 *
 * The inner `div` keeps `overflow-y-auto` as a fallback for routes that
 * render plain, unbounded content (the wizard, the home screen) — they still
 * scroll there. The matrix generation page is laid out to exactly fill it
 * (`flex-1 min-h-0` all the way down to its own grid, which owns the real
 * `overflow-auto`), so its `sticky top-0` header row and everything above
 * it (breadcrumb, title, toolbar) stay in place.
 */
export function RootComponent() {
  const { pathname } = useLocation();
  const accentNoyau = (useTweakByKey('matrix-noyaux-accent') ?? 'bleu-nuit') as NoyauColorToken;

  // /login renders the design system's AuthLayout, with no app chrome.
  // `SettingsShortcut` mounts outside both branches below so Ctrl/Cmd+Shift+.
  // is heard on every route; it renders nothing, and leaves `/login` to the
  // Tahiti encart.

  return (
    <>
      {pathname === '/login' ? (
        <Outlet />
      ) : (
        <MatricesShell>
          <div className="flex h-[calc(100vh-56px)] min-h-0 min-w-0 flex-col px-4 pt-3 md:px-6">
            <Breadcrumbs />
            <div
              data-testid="page-content"
              className={cn(
                'flex min-h-0 flex-1 flex-col overflow-y-auto',
                !pathname.startsWith('/matrices/') && 'py-6',
              )}
            >
              {isNoyauAccentScopedRoute(pathname) ? (
                <NoyauAccentScope noyau={accentNoyau}>
                  <Outlet />
                </NoyauAccentScope>
              ) : (
                <Outlet />
              )}
            </div>
          </div>
        </MatricesShell>
      )}
      <SettingsShortcut />
    </>
  );
}
